// Internationalisation seam.
//
// i18next is initialised at module load so the formatters in format.js and
// the imperative DOM strings in MapView can call `i18n.t` before React renders.
// Locale resolution order (plan: deployment config + navigator.language):
//   1. localStorage 'reporter:locale'  — an explicit user choice
//   2. navigator.language(s)           — best match against SUPPORTED_LOCALES
//   3. deployment default_locale        — applied by I18nProvider once config loads
//   4. FALLBACK_LOCALE ('en-GB')
//
// Only locales with both a UI catalog (common.json) and a taxonomy overlay
// (taxonomy.json, generated at build) are listed in SUPPORTED_LOCALES.

import { createElement, createContext, useContext, useEffect, useState } from 'react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enGB from '../locales/en-GB/common.json';
import enUS from '../locales/en-US/common.json';

export const SUPPORTED_LOCALES = ['en-GB', 'en-US'];
export const FALLBACK_LOCALE = 'en-GB';
export const LOCALE_STORAGE_KEY = 'reporter:locale';

const STORAGE = typeof localStorage !== 'undefined' ? localStorage : null;

function normalise(tag) {
  return String(tag || '').toLowerCase().replace('_', '-');
}

/** Resolve a single language tag to a supported locale, or null. */
export function matchLocale(tag) {
  if (!tag) return null;
  const norm = normalise(tag);
  const exact = SUPPORTED_LOCALES.find((l) => normalise(l) === norm);
  if (exact) return exact;
  const primary = norm.split('-')[0];
  return SUPPORTED_LOCALES.find((l) => normalise(l).startsWith(`${primary}-`)) ?? null;
}

function fromStorage() {
  return STORAGE ? matchLocale(STORAGE.getItem(LOCALE_STORAGE_KEY)) : null;
}

function fromBrowser() {
  if (typeof navigator === 'undefined') return null;
  const candidates = [...(navigator.languages || []), navigator.language].filter(Boolean);
  for (const c of candidates) {
    const m = matchLocale(c);
    if (m) return m;
  }
  return null;
}

/** The locale to activate on first paint, before the deployment default is known. */
export function detectInitialLocale() {
  return fromStorage() ?? fromBrowser() ?? FALLBACK_LOCALE;
}

i18n.use(initReactI18next).init({
  resources: {
    'en-GB': { common: enGB },
    'en-US': { common: enUS },
  },
  lng: detectInitialLocale(),
  fallbackLng: FALLBACK_LOCALE,
  defaultNS: 'common',
  ns: ['common'],
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  react: { useSuspense: false },
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.language;
}

/** Switch language, persist the choice, and keep <html lang> in sync. */
export function changeLocale(locale) {
  const resolved = matchLocale(locale) ?? FALLBACK_LOCALE;
  i18n.changeLanguage(resolved);
  if (STORAGE) STORAGE.setItem(LOCALE_STORAGE_KEY, resolved);
  if (typeof document !== 'undefined') document.documentElement.lang = resolved;
  return resolved;
}

export function getActiveLocale() {
  return i18n.language || FALLBACK_LOCALE;
}

/** True when the user has a storage choice or a browser language we matched. */
export function hasLocalePreference() {
  return Boolean(fromStorage() || fromBrowser());
}

const LocaleContext = createContext(FALLBACK_LOCALE);

/**
 * Wraps the app. Mirrors i18next language changes into React state so
 * locale-dependent computations (taxonomy overlay, Intl formatters) re-run.
 * The deployment default locale is applied separately — inside the app, where
 * `public_config()` is available.
 */
export function I18nProvider({ children }) {
  const [locale, setLocale] = useState(() => i18n.language || FALLBACK_LOCALE);

  useEffect(() => {
    const handler = (lng) => {
      setLocale(lng);
      if (typeof document !== 'undefined') document.documentElement.lang = lng;
    };
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, []);

  return createElement(LocaleContext.Provider, { value: locale }, children);
}

export function useLocale() {
  return useContext(LocaleContext);
}

export { i18n };
